import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Coffee, ExternalLink, Shield, RefreshCw } from 'lucide-react';
import { resetConsent } from './CookieConsent';

// ── Layout ──────────────────────────────────────────────────────────────────

const FooterContainer = styled.footer`
  background: #0d0d0d;
  color: #bbb;
  margin-top: auto;
`;

const Inner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const TopBar = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 48px;
  padding: 52px 0 44px;
  border-bottom: 1px solid #1e1e1e;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 36px;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

// ── Brand column ─────────────────────────────────────────────────────────────

const Brand = styled.div``;

const BrandName = styled.a`
  display: inline-block;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.3px;
  margin-bottom: 4px;
  text-decoration: none;

  &:hover { text-decoration: underline; }
`;

const BrandTagline = styled.div`
  font-size: 11px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #444;
  margin-bottom: 16px;
`;

const BrandDesc = styled.p`
  font-size: 13px;
  line-height: 1.65;
  color: #999;
  margin: 0 0 20px;
  max-width: 280px;
`;

const livePulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); }
  50%      { box-shadow: 0 0 0 5px rgba(34, 197, 94, 0); }
`;

const LiveBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 500;
  color: #888;
  margin-bottom: 20px;

  &::before {
    content: '';
    width: 7px;
    height: 7px;
    background: #22c55e;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5);
    animation: ${livePulse} 2s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before { animation: none; }
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 10px;
`;

const SocialBtn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #2a2a2a;
  color: #666;
  text-decoration: none;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: #fff;
    color: #fff;
  }

  svg { width: 15px; height: 15px; }
`;

// ── Link columns ─────────────────────────────────────────────────────────────

const Col = styled.div``;

const ColTitle = styled.h3`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: #fff;
  margin: 0 0 18px;
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const NavLink = styled.a`
  font-size: 13px;
  color: #aaa;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: color 0.15s;
  width: fit-content;

  &:hover { color: #fff; }

  svg { width: 12px; height: 12px; opacity: 0.6; }
`;

const CressiveBadge = styled.a`
  display: block;
  text-decoration: none;
  margin-top: 4px;

  img {
    display: block;
    width: 125px;
    height: auto;
    transition: transform 0.2s ease, filter 0.2s ease;
  }

  /* The logo alone gave no indication of what it covers. The caption states
     the scope in the reader's own words and matches the sibling link text. */
  .badge-caption {
    display: block;
    margin-top: 6px;
    font-size: 11.5px;
    line-height: 1.5;
    color: #8a8a8a;
  }

  &:hover img {
    transform: translateY(-2px);
    filter: brightness(1.3);
  }
  &:hover .badge-caption { color: #aaa; }
`;

const SupportBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  padding: 9px 14px;
  border: 1px solid #2a2a2a;
  border-radius: 6px;
  font-size: 12.5px;
  font-weight: 500;
  color: #aaa;
  text-decoration: none;
  width: fit-content;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  &:hover {
    border-color: #FFDD00;
    color: #FFDD00;
    background: rgba(255, 221, 0, 0.06);
  }

  svg { width: 14px; height: 14px; }
`;

const NavBtn = styled.button`
  font-size: 13px;
  color: #aaa;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: color 0.15s;

  &:hover { color: #fff; }
`;

// ── OGL strip ────────────────────────────────────────────────────────────────

const OGLStrip = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 0;
  border-bottom: 1px solid #1e1e1e;

  svg {
    width: 16px;
    height: 16px;
    color: #444;
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const OGLText = styled.p`
  font-size: 12px;
  color: #777;
  line-height: 1.6;
  margin: 0;

  a {
    color: #999;
    text-decoration: none;
    border-bottom: 1px solid #444;
    transition: color 0.15s, border-color 0.15s;

    &:hover { color: #fff; border-color: #fff; }
  }
`;

// ── Bottom bar ────────────────────────────────────────────────────────────────

const BottomBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  font-size: 12px;
  color: #666;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const BuiltBy = styled.div`
  a {
    color: #888;
    text-decoration: none;
    transition: color 0.15s;
    &:hover { color: #fff; }
  }
`;

const BottomRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  a {
    color: #666;
    text-decoration: none;
    transition: color 0.15s;
    &:hover { color: #aaa; }
  }
`;

// ── Component ────────────────────────────────────────────────────────────────

/* lucide ships the retired bird as its Twitter icon, so the current X mark is
   drawn here instead, sized to sit with the other social icons. */
const XIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <Inner>
        <TopBar>

          {/* Brand */}
          <Brand>
            <BrandName href="/">UK Petition Tracker</BrandName>
            <BrandTagline>Democracy in Numbers</BrandTagline>
            <BrandDesc>
              Real-time analytics for UK Parliament and Government petitions.
              Track signatures, government responses, debates, and regional data —
              completely free, no account needed.
            </BrandDesc>
            <LiveBadge>Live petition data, updated throughout the day</LiveBadge>
            <SocialRow>
              <SocialBtn href="https://x.com/petitiontracker" target="_blank" rel="noopener noreferrer" aria-label="UK Petition Tracker on X">
                <XIcon />
              </SocialBtn>
            </SocialRow>
            <SupportBtn
              href="https://buymeacoffee.com/dostogir"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Coffee /> Support the site
            </SupportBtn>
          </Brand>

          {/* Explore */}
          <Col>
            <ColTitle>Explore</ColTitle>
            <LinkList>
              <NavLink href="/petitions/trending">Trending</NavLink>
              <NavLink href="/petitions/open">Open Petitions</NavLink>
              <NavLink href="/petitions/awaiting-response">Awaiting Response</NavLink>
              <NavLink href="/petitions/debate-scheduled">Debate Scheduled</NavLink>
              <NavLink href="/petitions/closed">Closed</NavLink>
              <NavLink href="/petitions/rejected">Rejected</NavLink>
              <NavLink href="/petitions/local">Local Petitions</NavLink>
            </LinkList>
          </Col>

          {/* Company */}
          <Col>
            <ColTitle>Company</ColTitle>
            <LinkList>
              <NavLink href="/about">About</NavLink>
              <NavLink href="/insights">Insights</NavLink>
              <NavLink href="/whats-new">What&rsquo;s New</NavLink>
              <NavLink href="/faq">FAQ</NavLink>
              <NavLink href="/contact">Contact</NavLink>
              <NavLink href="/privacy">Privacy Policy</NavLink>
              <NavLink href="/terms">Terms of Use</NavLink>
              <NavBtn onClick={resetConsent}>Cookie Preferences</NavBtn>
            </LinkList>
          </Col>

          {/* Data */}
          <Col>
            <ColTitle>Data</ColTitle>
            <LinkList>
              <NavLink href="https://petition.parliament.uk" target="_blank" rel="noopener noreferrer">
                Official Petitions <ExternalLink />
              </NavLink>
              <NavLink href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer">
                OGL v3.0 Licence <ExternalLink />
              </NavLink>
              <NavLink href="https://github.com/martinjc/UK-GeoJSON" target="_blank" rel="noopener noreferrer">
                Map Data Source <ExternalLink />
              </NavLink>
              <NavLink href="https://railway.app" target="_blank" rel="noopener noreferrer">
                Hosted on Railway <ExternalLink />
              </NavLink>
              <CressiveBadge
                href="https://cressive.com/privacy-compliance/"
                target="_blank"
                rel="noopener noreferrer"
                title="Cressive DX scan this site for cookie and tracking issues under UK GDPR and the ePrivacy Directive, and advise on fixes"
              >
                <img
                  src="https://cdn.cressive.com/badges/cpc_logo_base_invert.svg"
                  alt="Cressive Privacy Compliance — privacy compliance support by Cressive DX"
                  width="80"
                  height="28"
                  loading="lazy"
                />
                <span className="badge-caption">
                  Privacy compliance support by Cressive DX
                </span>
              </CressiveBadge>
            </LinkList>
          </Col>

        </TopBar>

        {/* OGL notice */}
        <OGLStrip>
          <Shield />
          <OGLText>
            All petition data is sourced from the{' '}
            <a href="https://petition.parliament.uk" target="_blank" rel="noopener noreferrer">official UK Parliament API</a>
            {' '}and published under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer">Open Government Licence v3.0</a>.
            {' '}Map boundary data © Crown copyright and database right 2013, sourced via{' '}
            <a href="https://github.com/martinjc/UK-GeoJSON" target="_blank" rel="noopener noreferrer">martinjc/UK-GeoJSON</a>,
            also under OGL v3. Provided for analytics and reference only — verify critical information with official sources.
          </OGLText>
        </OGLStrip>

        {/* Bottom bar */}
        <BottomBar>
          <BuiltBy>
            © {currentYear} UK Petition Tracker · Built by{' '}
            <a href="https://www.linkedin.com/in/gdapu/" target="_blank" rel="noopener noreferrer">
              Mohammad Golam Dostogir
            </a>
            {' '}· Made in Guildford, Surrey 🇬🇧
          </BuiltBy>
          <BottomRight>
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="/contact">Contact</a>
            <a href="https://x.com/petitiontracker" target="_blank" rel="noopener noreferrer">X</a>
          </BottomRight>
        </BottomBar>

      </Inner>
    </FooterContainer>
  );
};

export default Footer;
